import type { DateRange, DateRangePreset } from './types';

export function formatDate(d: Date): string {
    return d.toISOString().split('T')[0];
}

function today(): DateRange {
    const now = formatDate(new Date());
    return { start: now, end: now };
}

function yesterday(): DateRange {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    const date = formatDate(d);
    return { start: date, end: date };
}

function last7Days(): DateRange {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 6);
    return { start: formatDate(start), end: formatDate(end) };
}

function last30Days(): DateRange {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 29);
    return { start: formatDate(start), end: formatDate(end) };
}

function thisMonth(): DateRange {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    return { start: formatDate(start), end: formatDate(now) };
}

function lastMonth(): DateRange {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const end = new Date(now.getFullYear(), now.getMonth(), 0);
    return { start: formatDate(start), end: formatDate(end) };
}

function thisYear(): DateRange {
    const now = new Date();
    const start = new Date(now.getFullYear(), 0, 1);
    return { start: formatDate(start), end: formatDate(now) };
}

/** Date range presets for the dashboard filter */
export const DATE_RANGE_PRESETS: DateRangePreset[] = [
    { key: 'today', labelKey: 'dateRanges.today', getRange: today },
    { key: 'yesterday', labelKey: 'dateRanges.yesterday', getRange: yesterday },
    { key: 'last7days', labelKey: 'dateRanges.last7Days', getRange: last7Days },
    { key: 'last30days', labelKey: 'dateRanges.last30Days', getRange: last30Days },
    { key: 'thisMonth', labelKey: 'dateRanges.thisMonth', getRange: thisMonth },
    { key: 'lastMonth', labelKey: 'dateRanges.lastMonth', getRange: lastMonth },
    { key: 'thisYear', labelKey: 'dateRanges.thisYear', getRange: thisYear },
];

/** Default date range: last 30 days */
export const DEFAULT_DATE_RANGE = last30Days;

/**
 * Resolve a DateRange from URL search params.
 * Supports preset keys (e.g. 'last30days') and custom ranges via `start`+`end`.
 */
export function getDateRangeFromSearchParams(searchParams: { get: (key: string) => string | null }): DateRange {
    const rangeKey = searchParams.get('range') || 'last30days';
    if (rangeKey === 'custom') {
        const start = searchParams.get('start');
        const end = searchParams.get('end');
        if (start && end) {
            return { start, end };
        }
    }
    const preset = DATE_RANGE_PRESETS.find((p) => p.key === rangeKey);
    return preset ? preset.getRange() : DEFAULT_DATE_RANGE();
}
